import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  Box,
  Chip,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { listRedirects, createRedirect, updateRedirect, deleteRedirect } from './seoApi';

const BLANK = { from: '', to: '', statusCode: 301, notes: '' };

export default function Redirects() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const res = await listRedirects();
    if (res?.status === 200) setRows(res.data.redirects || []);
    else toast.error(res?.data?.message || 'Failed to load redirects');
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setForm(BLANK);
    setErrors([]);
    setDialogOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    setErrors([]);
    const res = await createRedirect(form);
    setSubmitting(false);
    if (res?.status === 201) {
      toast.success('Redirect created');
      setDialogOpen(false);
      load();
    } else if (res?.status === 400 && res.data?.errors) {
      setErrors(res.data.errors);
    } else {
      toast.error(res?.data?.message || 'Failed to create redirect');
    }
  };

  const toggleEnabled = async (row) => {
    const res = await updateRedirect(row._id, { enabled: !row.enabled });
    if (res?.status === 200) {
      setRows((prev) => prev.map((r) => (r._id === row._id ? { ...r, enabled: !r.enabled } : r)));
    } else {
      toast.error(res?.data?.message || 'Failed to update redirect');
    }
  };

  const remove = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete redirect from ${row.from}?`)) return;
    const res = await deleteRedirect(row._id);
    if (res?.status === 200) {
      toast.success('Redirect deleted');
      setRows((prev) => prev.filter((r) => r._id !== row._id));
    } else {
      toast.error(res?.data?.message || 'Failed to delete redirect');
    }
  };

  return (
    <Container maxWidth="lg">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Redirects</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/dashboard/seo')}>Back to pages</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>Add redirect</Button>
        </Stack>
      </Stack>

      <Card>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      No redirects yet.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r._id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.from}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.to}</TableCell>
                    <TableCell>
                      <Chip size="small" label={`${r.statusCode}`} />
                    </TableCell>
                    <TableCell>
                      <Switch checked={!!r.enabled} onChange={() => toggleEnabled(r)} size="small" />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', maxWidth: 200 }} noWrap>{r.notes || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton color="error" onClick={() => remove(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add redirect</DialogTitle>
        <DialogContent dividers>
          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </Alert>
          )}
          <Stack spacing={2} mt={1}>
            <TextField
              label="From (old path)"
              placeholder="/old-page"
              value={form.from}
              onChange={(e) => setForm({ ...form, from: e.target.value })}
              fullWidth
              size="small"
              helperText="The path to redirect away from, e.g. /old-page"
            />
            <TextField
              label="To (new destination)"
              placeholder="/new-page or https://…"
              value={form.to}
              onChange={(e) => setForm({ ...form, to: e.target.value })}
              fullWidth
              size="small"
              helperText="An internal path or an https:// URL"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={form.statusCode}
                onChange={(e) => setForm({ ...form, statusCode: e.target.value })}
              >
                <MenuItem value={301}>301 — Permanent</MenuItem>
                <MenuItem value={302}>302 — Temporary</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
